import { makeBlobRequest, BlobItem } from './blobService';

export interface ProductImage {
  url: string;
  type: 'main' | 'detail';
  alt: string;
  order?: number;
}

export interface ProductComparison {
  original: {
    price: number;
    material: string;
    craftsmanship: string;
  };
  replica: {
    price: number;
    material: string;
    craftsmanship: string;
  };
}

export interface Product {
  id: string;
  title: string;
  category: string;
  price: number;
  currency: string;
  images: ProductImage[];
  tags: string[];
  description: string;
  sizes: string[];
  materials: string[];
  comparison: ProductComparison;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  name: string;
  description: string;
  image: string;
  order: number;
}

// 添加新的接口定义
export interface GetProductsParams {
  page?: number;
  pageSize?: number;
  category?: string;
}

// 修改 getProducts 函数签名和实现
export const getProducts = async (params?: GetProductsParams) => {
  try {
    console.log('ProductService - Fetching products:', params);
    
    const blobParams: Record<string, string> = {
      prefix: 'products/metadata/'
    };

    const response = await makeBlobRequest(blobParams);
    console.log('ProductService - Blob response:', response);

    if (!response.blobs || response.blobs.length === 0) {
      console.warn('ProductService - No product metadata files found');
      return { products: [], totalProducts: 0 };
    }

    // 打印所有 JSON 文件名
    console.log('ProductService - Found JSON files:', 
      response.blobs
        .filter(blob => blob.pathname.endsWith('.json'))
        .map(blob => ({
          pathname: blob.pathname,
          filename: blob.pathname.split('/').pop(),
          uploadedAt: blob.uploadedAt
        }))
    );

    // 按商品ID分组并获取最新版本
    const productGroups = new Map<string, BlobItem>();
    response.blobs
      .filter(blob => blob.pathname.endsWith('.json'))
      .forEach(blob => {
        const pathParts = blob.pathname.split('/');
        const fileName = pathParts[pathParts.length - 1];
        const matches = fileName.match(/^(product-\d+)/);
        const productId = matches ? matches[1] : null;
        
        if (!productId) {
          console.log('ProductService - Skipping file with invalid name format:', fileName);
          return;
        }
        
        const existingBlob = productGroups.get(productId);
        const currentDate = blob.uploadedAt ? new Date(blob.uploadedAt) : new Date(0);
        const existingDate = existingBlob?.uploadedAt ? new Date(existingBlob.uploadedAt) : new Date(0);
        
        if (!existingBlob || currentDate > existingDate) {
          productGroups.set(productId, blob);
          console.log('ProductService - Updated product version:', {
            productId,
            fileName,
            uploadedAt: blob.uploadedAt
          });
        }
      });

    const products = await Promise.all(
      Array.from(productGroups.values()).map(async (blob: BlobItem) => {
        try {
          const productResponse = await fetch(blob.url);
          if (!productResponse.ok) return null;
          const product = await productResponse.json() as Product;
          return product;
        } catch (error) {
          console.error('ProductService - Error fetching product data:', error);
          return null;
        }
      })
    );

    // 过滤有效商品
    let validProducts = products.filter((product): product is Product => 
      product !== null && product.status === 'active'
    );

    // 按上传时间排序（最新的在前面）
    validProducts.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });

    // 按分类筛选
    if (params?.category && params.category !== 'all') {
      validProducts = validProducts.filter(product => product.category === params.category);
    }

    // 分页处理
    const totalProducts = validProducts.length;
    const page = params?.page || 1;
    const pageSize = params?.pageSize || 12;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const paginatedProducts = validProducts.slice(start, end);

    console.log('ProductService - Final products:', {
      totalProducts,
      currentPage: page,
      pageSize,
      products: paginatedProducts
    });

    return {
      products: paginatedProducts,
      totalProducts,
      currentPage: page,
      pageSize
    };
  } catch (error) {
    console.error('ProductService - Error fetching products:', error);
    return { products: [], totalProducts: 0 };
  }
};

// 获取单个商品详情
export const getProductById = async (productId: string): Promise<Product | null> => {
  try {
    console.log('ProductService - Fetching product:', productId);
    
    // 修改为只匹配目录前缀，然后在结果中查找对应的商品ID
    const params: Record<string, string> = {
      prefix: 'products/metadata/'
    };

    const response = await makeBlobRequest(params);
    console.log('ProductService - Blob response:', response);

    if (!response.blobs || response.blobs.length === 0) {
      console.warn('ProductService - No products found');
      return null;
    }

    // 查找匹配的商品文件
    const productFile = response.blobs.find(blob => 
      blob.pathname.startsWith(`products/metadata/${productId}`) && 
      blob.pathname.endsWith('.json')
    );

    if (!productFile) {
      console.warn('ProductService - Product not found:', productId);
      return null;
    }

    try {
      console.log('ProductService - Fetching from:', productFile.url);
      const productResponse = await fetch(productFile.url);
      if (!productResponse.ok) {
        throw new Error(`HTTP error! status: ${productResponse.status}`);
      }

      const text = await productResponse.text();
      console.log('ProductService - Raw product data:', text);

      const product = JSON.parse(text) as Product;
      console.log('ProductService - Parsed product:', product);
      return product;
    } catch (parseError) {
      console.error('ProductService - Error parsing product data:', parseError);
      return null;
    }
  } catch (error) {
    console.error('ProductService - Error fetching product:', error);
    return null;
  }
};

// 默认分类图片
const DEFAULT_CATEGORY_IMAGE = '/assets/banners/微信图片_20250218004120.jpg';

// 获取分类列表
export const getCategories = async (): Promise<Category[]> => {
  try {
    const params: Record<string, string> = {
      prefix: 'products/categories/'
    };
    
    const response = await makeBlobRequest(params);
    console.log('CategoryService - Blob response:', response);

    // 如果没有找到任何分类文件，返回默认分类
    if (!response.blobs || response.blobs.length === 0) {
      console.log('CategoryService - No categories found, returning defaults');
      return [
        {
          id: 'sneakers',
          name: '球鞋',
          description: '运动鞋和潮流球鞋',
          image: DEFAULT_CATEGORY_IMAGE,
          order: 1
        },
        {
          id: 'clothing',
          name: '服饰',
          description: '潮流服装',
          image: DEFAULT_CATEGORY_IMAGE,
          order: 2
        }
      ];
    }

    // 查找最新的分类文件
    const categoryFiles = response.blobs
      .filter(blob => blob.pathname.endsWith('.json'))
      .sort((a, b) => {
        const dateA = new Date(a.uploadedAt || 0);
        const dateB = new Date(b.uploadedAt || 0);
        return dateB.getTime() - dateA.getTime();
      });

    if (categoryFiles.length > 0) {
      const latestFile = categoryFiles[0];
      console.log('CategoryService - Found latest category file:', latestFile);
      const categoryResponse = await fetch(latestFile.url);
      if (categoryResponse.ok) {
        const data = await categoryResponse.json();
        return data.categories || [];
      }
    }

    console.log('CategoryService - No valid category file found, returning defaults');
    return [
      {
        id: 'sneakers',
        name: '球鞋',
        description: '运动鞋和潮流球鞋',
        image: DEFAULT_CATEGORY_IMAGE,
        order: 1
      },
      {
        id: 'clothing',
        name: '服饰',
        description: '潮流服装',
        image: DEFAULT_CATEGORY_IMAGE,
        order: 2
      }
    ];
  } catch (error) {
    console.error('CategoryService - Error fetching categories:', error);
    return [
      {
        id: 'sneakers',
        name: '球鞋',
        description: '运动鞋和潮流球鞋',
        image: DEFAULT_CATEGORY_IMAGE,
        order: 1
      },
      {
        id: 'clothing',
        name: '服饰',
        description: '潮流服装',
        image: DEFAULT_CATEGORY_IMAGE,
        order: 2
      }
    ];
  }
};

// 删除旧的分类文件
const deleteOldCategoryFiles = async (token: string): Promise<boolean> => {
  try {
    const params: Record<string, string> = {
      prefix: 'products/categories/'
    };
    
    const response = await makeBlobRequest(params);
    if (!response.blobs || response.blobs.length === 0) {
      return true;
    }

    const filesToDelete = response.blobs
      .filter(blob => blob.pathname.endsWith('.json'))
      .map(blob => blob.url);

    if (filesToDelete.length > 0) {
      console.log('CategoryService - Deleting old category files:', filesToDelete);
      
      const deleteResponse = await fetch('/blob-api/delete', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ urls: filesToDelete }),
      });

      if (!deleteResponse.ok) {
        console.error('CategoryService - Failed to delete old files:', deleteResponse.status);
        return false;
      }
      
      console.log('CategoryService - Old category files deleted successfully');
    }

    return true;
  } catch (error) {
    console.error('CategoryService - Error deleting old category files:', error);
    return false;
  }
};

// 上传分类图片
const uploadCategoryImage = async (file: File, token: string): Promise<string> => {
  try {
    // 生成文件名，使用时间戳和原始文件名
    const timestamp = new Date().getTime();
    const extension = file.name.split('.').pop();
    const fileName = `category-${timestamp}.${extension}`;
    const filePath = `products/categories/images/${fileName}`;

    console.log('CategoryService - Uploading image:', {
      originalName: file.name,
      newName: fileName,
      path: filePath
    });

    // 创建 FormData
    const formData = new FormData();
    formData.append('file', file);

    // 上传图片
    const response = await fetch(`/blob-api/${filePath}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: file
    });

    if (!response.ok) {
      throw new Error(`Upload error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('CategoryService - Image uploaded successfully:', result);

    // 返回图片的完整URL
    return `/blob-api/${filePath}`;
  } catch (error) {
    console.error('CategoryService - Error uploading image:', error);
    throw error;
  }
};

// 创建新分类
export const createCategory = async (category: Category, imageFile?: File): Promise<boolean> => {
  try {
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    if (!token) {
      throw new Error('Blob token not found');
    }

    console.log('CategoryService - Creating new category:', category);

    // 处理图片上传
    let imageUrl = category.image;
    if (imageFile) {
      try {
        imageUrl = await uploadCategoryImage(imageFile, token);
      } catch (error) {
        console.error('CategoryService - Failed to upload image:', error);
        imageUrl = DEFAULT_CATEGORY_IMAGE;
      }
    }

    // 如果没有提供图片，使用默认图片
    const categoryData = {
      ...category,
      image: imageUrl || DEFAULT_CATEGORY_IMAGE
    };

    // 获取当前分类列表
    const currentCategories = await getCategories();
    const updatedCategories = {
      categories: [...currentCategories, categoryData],
      updatedAt: new Date().toISOString()
    };

    // 删除旧的分类文件
    const deleteSuccess = await deleteOldCategoryFiles(token);
    if (!deleteSuccess) {
      console.error('CategoryService - Failed to delete old category files');
      return false;
    }

    // 生成新的文件名
    const filePath = `products/categories/categories.json`;

    console.log('CategoryService - Uploading categories:', {
      path: filePath,
      categories: updatedCategories
    });

    // 上传更新后的分类列表
    const response = await fetch(`/blob-api/${filePath}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedCategories),
    });

    if (!response.ok) {
      console.error('CategoryService - Failed to create category:', response.status, response.statusText);
      return false;
    }

    console.log('CategoryService - Category created successfully');
    return true;
  } catch (error) {
    console.error('CategoryService - Error creating category:', error);
    return false;
  }
};

// 更新分类
export const updateCategory = async (categoryId: string, category: Category, imageFile?: File): Promise<boolean> => {
  try {
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    if (!token) {
      throw new Error('Blob token not found');
    }

    console.log('CategoryService - Updating category:', { categoryId, category });

    // 处理图片上传
    let imageUrl = category.image;
    if (imageFile) {
      try {
        imageUrl = await uploadCategoryImage(imageFile, token);
      } catch (error) {
        console.error('CategoryService - Failed to upload image:', error);
        // 如果上传失败，保留原有图片
        imageUrl = category.image;
      }
    }

    // 如果没有提供图片，使用默认图片
    const categoryData = {
      ...category,
      image: imageUrl || DEFAULT_CATEGORY_IMAGE
    };

    // 获取当前分类列表
    const currentCategories = await getCategories();
    const updatedCategories = {
      categories: currentCategories.map(c => c.id === categoryId ? categoryData : c),
      updatedAt: new Date().toISOString()
    };

    // 删除旧的分类文件
    const deleteSuccess = await deleteOldCategoryFiles(token);
    if (!deleteSuccess) {
      console.error('CategoryService - Failed to delete old category files');
      return false;
    }

    // 生成新的文件名
    const filePath = `products/categories/categories.json`;

    console.log('CategoryService - Uploading updated categories:', {
      path: filePath,
      categories: updatedCategories
    });

    // 上传更新后的分类列表
    const response = await fetch(`/blob-api/${filePath}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedCategories),
    });

    if (!response.ok) {
      console.error('CategoryService - Failed to update category:', response.status, response.statusText);
      return false;
    }

    console.log('CategoryService - Category updated successfully');
    return true;
  } catch (error) {
    console.error('CategoryService - Error updating category:', error);
    return false;
  }
};

// 删除分类
export const deleteCategory = async (categoryId: string): Promise<boolean> => {
  try {
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    if (!token) {
      throw new Error('Blob token not found');
    }

    console.log('CategoryService - Deleting category:', categoryId);

    // 获取当前分类列表
    const currentCategories = await getCategories();
    const updatedCategories = {
      categories: currentCategories.filter(c => c.id !== categoryId),
      updatedAt: new Date().toISOString()
    };

    // 删除旧的分类文件
    const deleteSuccess = await deleteOldCategoryFiles(token);
    if (!deleteSuccess) {
      console.error('CategoryService - Failed to delete old category files');
      return false;
    }

    // 生成新的文件名
    const filePath = `products/categories/categories.json`;

    console.log('CategoryService - Uploading categories after deletion:', {
      path: filePath,
      categories: updatedCategories
    });

    // 上传更新后的分类列表
    const response = await fetch(`/blob-api/${filePath}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedCategories),
    });

    if (!response.ok) {
      console.error('CategoryService - Failed to delete category:', response.status, response.statusText);
      return false;
    }

    console.log('CategoryService - Category deleted successfully');
    return true;
  } catch (error) {
    console.error('CategoryService - Error deleting category:', error);
    return false;
  }
};

// 更新商品信息
export const updateProduct = async (productId: string, productData: Partial<Product>): Promise<boolean> => {
  try {
    console.log('ProductService - Updating product:', { productId, productData });
    
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    if (!token) {
      throw new Error('Blob token not found');
    }

    // 1. 获取当前商品的所有信息
    const currentProduct = await getProductById(productId);
    if (!currentProduct) {
      throw new Error('Current product not found');
    }

    // 2. 获取所有相关文件（包括元数据和图片）
    const metadataParams: Record<string, string> = {
      prefix: `products/metadata/${productId}`
    };
    const imagesParams: Record<string, string> = {
      prefix: `products/images/${productId}`
    };

    const [metadataResponse, imagesResponse] = await Promise.all([
      makeBlobRequest(metadataParams),
      makeBlobRequest(imagesParams)
    ]);

    console.log('ProductService - Current files:', {
      metadata: metadataResponse,
      images: imagesResponse
    });

    // 3. 找出需要删除的文件
    const filesToDelete: string[] = [];

    // 添加所有旧的 JSON 文件到删除列表
    if (metadataResponse.blobs && metadataResponse.blobs.length > 0) {
      filesToDelete.push(...metadataResponse.blobs.map(blob => blob.url));
    }

    // 找出需要删除的图片
    if (imagesResponse.blobs && imagesResponse.blobs.length > 0 && productData.images) {
      const currentImageUrls = new Set(productData.images.map(img => img.url));
      const unusedImages = imagesResponse.blobs.filter(blob => !currentImageUrls.has(blob.url));
      filesToDelete.push(...unusedImages.map(blob => blob.url));
    }

    // 4. 删除所有不需要的文件
    if (filesToDelete.length > 0) {
      const deleteBody = {
        urls: filesToDelete
      };

      console.log('ProductService - Deleting old files:', {
        count: filesToDelete.length,
        files: filesToDelete
      });

      const deleteResponse = await fetch('/blob-api/delete', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(deleteBody)
      });

      if (!deleteResponse.ok) {
        console.error('Failed to delete old files:', deleteResponse.status);
      } else {
        console.log('ProductService - Old files deleted successfully');
      }
    }

    // 5. 创建新的商品元数据文件
    const fileName = `${productId}.json`;
    const filePath = `products/metadata/${fileName}`;

    // 确保更新的数据包含必要的字段
    const updatedProduct: Product = {
      ...currentProduct,
      ...productData as Partial<Product>,
      id: productId,
      updatedAt: new Date().toISOString()
    };

    console.log('ProductService - Creating new version:', {
      path: filePath,
      data: updatedProduct
    });

    // 6. 上传新版本
    const uploadResponse = await fetch(`/blob-api/${filePath}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedProduct),
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload error! status: ${uploadResponse.status}`);
    }

    const result = await uploadResponse.json();
    console.log('ProductService - Product updated successfully:', result);

    return true;
  } catch (error) {
    console.error('ProductService - Error updating product:', error);
    return false;
  }
};

// 创建新商品
export const createProduct = async (productData: Product): Promise<string | null> => {
  try {
    console.log('ProductService - Creating new product:', productData);
    
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    if (!token) {
      throw new Error('Blob token not found');
    }

    // 生成文件名，使用传入的商品ID
    const fileName = `${productData.id}.json`;
    const filePath = `products/metadata/${fileName}`;

    // 使用代理路径上传
    const response = await fetch(`/blob-api/${filePath}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('ProductService - Product created successfully:', result);
    return productData.id;
  } catch (error) {
    console.error('ProductService - Error creating product:', error);
    return null;
  }
};

// 删除商品
export const deleteProduct = async (productId: string): Promise<boolean> => {
  try {
    console.log('ProductService - Deleting product:', productId);
    
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    if (!token) {
      throw new Error('Blob token not found');
    }
    console.log('ProductService - Token check:', {
      exists: !!token,
      length: token.length,
      prefix: token.substring(0, 10) + '...'
    });

    // 1. 获取所有相关文件（包括元数据和图片）
    const metadataParams: Record<string, string> = {
      prefix: `products/metadata/${productId}`
    };
    const imagesParams: Record<string, string> = {
      prefix: `products/images/${productId}`
    };
    console.log('ProductService - Search params:', { metadataParams, imagesParams });

    const [metadataResponse, imagesResponse] = await Promise.all([
      makeBlobRequest(metadataParams),
      makeBlobRequest(imagesParams)
    ]);

    console.log('ProductService - Files to check for deletion:', {
      metadata: {
        hasMore: metadataResponse.hasMore,
        blobCount: metadataResponse.blobs?.length || 0,
        blobs: metadataResponse.blobs?.map(blob => ({
          pathname: blob.pathname,
          url: blob.url,
          uploadedAt: blob.uploadedAt
        }))
      },
      images: {
        hasMore: imagesResponse.hasMore,
        blobCount: imagesResponse.blobs?.length || 0,
        blobs: imagesResponse.blobs?.map(blob => ({
          pathname: blob.pathname,
          url: blob.url,
          uploadedAt: blob.uploadedAt
        }))
      }
    });

    if ((!metadataResponse.blobs || metadataResponse.blobs.length === 0) &&
        (!imagesResponse.blobs || imagesResponse.blobs.length === 0)) {
      console.error('ProductService - No files found for product:', productId);
      return false;
    }

    // 2. 合并所有需要删除的文件
    const allBlobs = [...(metadataResponse.blobs || []), ...(imagesResponse.blobs || [])];
    const filesToDelete = allBlobs.filter(blob => {
      const shouldDelete = blob.pathname.includes(productId);
      console.log('ProductService - File filter:', {
        pathname: blob.pathname,
        shouldDelete,
        matchPattern: productId
      });
      return shouldDelete;
    });

    console.log('ProductService - Files to delete:', {
      count: filesToDelete.length,
      files: filesToDelete.map(file => ({
        pathname: file.pathname,
        url: file.url
      }))
    });

    // 3. 批量删除所有文件
    if (filesToDelete.length > 0) {
      try {
        console.log('ProductService - Attempting to delete files:', {
          count: filesToDelete.length,
          files: filesToDelete.map(file => file.pathname)
        });
        
        // 构建删除请求体
        const deleteBody = {
          urls: filesToDelete.map(file => file.url)
        };
        
        console.log('ProductService - Delete request:', {
          url: '/blob-api/delete',
          method: 'POST',
          headers: {
            'Authorization': 'Bearer [hidden]',
            'Content-Type': 'application/json'
          },
          body: deleteBody
        });
        
        const response = await fetch('/blob-api/delete', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(deleteBody)
        });

        console.log('ProductService - Delete response:', {
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries())
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('ProductService - Delete error details:', {
            status: response.status,
            error: errorText,
            headers: Object.fromEntries(response.headers.entries())
          });
          return false;
        }

        const result = await response.json();
        console.log('ProductService - Delete success:', {
          filesDeleted: result.length,
          result
        });
      } catch (error) {
        console.error('ProductService - Delete operation error:', {
          error: error instanceof Error ? {
            message: error.message,
            stack: error.stack
          } : error
        });
        return false;
      }
    }

    // 4. 删除完成后，再次检查文件是否还存在
    console.log('ProductService - Verifying deletion...');
    const [verifyMetadata, verifyImages] = await Promise.all([
      makeBlobRequest(metadataParams),
      makeBlobRequest(imagesParams)
    ]);

    console.log('ProductService - Verification result:', {
      metadata: {
        hasMore: verifyMetadata.hasMore,
        remainingFiles: verifyMetadata.blobs?.map(blob => blob.pathname) || []
      },
      images: {
        hasMore: verifyImages.hasMore,
        remainingFiles: verifyImages.blobs?.map(blob => blob.pathname) || []
      }
    });

    if ((verifyMetadata.blobs && verifyMetadata.blobs.length > 0) ||
        (verifyImages.blobs && verifyImages.blobs.length > 0)) {
      console.warn('ProductService - Deletion verification failed:', {
        expectedEmpty: true,
        remainingMetadataFiles: verifyMetadata.blobs?.map(blob => blob.pathname) || [],
        remainingImageFiles: verifyImages.blobs?.map(blob => blob.pathname) || []
      });
      return false;
    }

    console.log('ProductService - Product deleted successfully');
    return true;
  } catch (error) {
    console.error('ProductService - Delete operation failed:', {
      productId,
      error: error instanceof Error ? {
        message: error.message,
        stack: error.stack
      } : error
    });
    return false;
  }
};